import React, {
  useState,
  useEffect,
  useRef,
} from 'react';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Switch,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Paper,
  alpha,
} from '@mui/material';
import {
  CheckCircle as SelectedIcon,
  People as TeamIcon,
} from '@mui/icons-material';
import gulagService from '../../services/gulag';
import {
  getGameImage,
} from '../../utils/gameImages';

function GamePreferenceSelector({ eventId }) {
    const scrollContainerRef = useRef(null);
    const [games, setGames] = useState([]);
    const [preferences, setPreferences] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [expandedGame, setExpandedGame] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError(null);

        try {
            // Load all active games where challenges are allowed
            const gamesResult = await gulagService.getGames('active');
            if (gamesResult.success) {
                // Filter to only show games where challenges are allowed
                const challengeEnabledGames = gamesResult.data.filter(
                    game => game.challenges_allowed !== false
                );
                setGames(challengeEnabledGames);
            } else {
                setError(gamesResult.error);
            }

            // Load user's preferences
            const prefsResult = await gulagService.getMyPreferences();
            if (prefsResult.success) {
                setPreferences(prefsResult.data);
            }
        } catch (err) {
            console.error('Error loading data:', err);
            setError('Failed to load games and preferences');
        } finally {
            setLoading(false);
        }
    };

    const isGameSelected = (gameId) => {
        return preferences.some(pref => pref.game_id === gameId);
    };

    const getGamePreference = (gameId) => {
        return preferences.find(pref => pref.game_id === gameId);
    };

    const handleToggleGame = async (game) => {
        const isSelected = isGameSelected(game.id);

        try {
            if (isSelected) {
                // Remove preference
                const result = await gulagService.removeGamePreference(game.id);
                if (result.success) {
                    setSuccess(`Removed ${game.name} from your preferences`);
                    await loadData();
                } else {
                    setError(result.error);
                }
            } else {
                // Add preference
                const preferenceData = {
                    game_id: game.id,
                    available: true,
                    team_ids: [],
                    notes: ''
                };
                const result = await gulagService.setGamePreference(game.id, preferenceData);
                if (result.success) {
                    setSuccess(`Added ${game.name} to your preferences`);
                    await loadData();
                } else {
                    setError(result.error);
                }
            }
        } catch (err) {
            console.error('Error toggling game preference:', err);
            setError('Failed to update preference');
        }
    };

    const handleToggleAvailability = async (gameId, currentAvailability) => {
        try {
            const result = await gulagService.toggleGameAvailability(gameId, !currentAvailability);
            if (result.success) {
                setSuccess(currentAvailability ? 'You are now unavailable for this game' : 'You are now available for this game');
                await loadData();
            } else {
                setError(result.error);
            }
        } catch (err) {
            console.error('Error toggling availability:', err);
            setError('Failed to toggle availability');
        }
    };

    const getCategoryColor = (category) => {
        switch (category) {
            case 'casual': return 'success';
            case 'competitive': return 'error';
            case 'irl': return 'warning';
            default: return 'default';
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {games.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        No games available yet. Check back later!
                    </Typography>
                </Paper>
            ) : (
                <Box
                    ref={scrollContainerRef}
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: 'repeat(2, 1fr)',
                            sm: 'repeat(3, 1fr)',
                            md: 'repeat(4, 1fr)',
                            lg: 'repeat(6, 1fr)',
                            xl: 'repeat(8, 1fr)'
                        },
                        gap: 2,
                        pb: 2
                    }}
                >
                    {games.map((game) => {
                        const isSelected = isGameSelected(game.id);
                        const preference = getGamePreference(game.id);
                        const isExpanded = expandedGame === game.id;

                        return (
                            <Card
                                key={game.id}
                                sx={{
                                    position: 'relative',
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    border: isSelected ? 3 : 1,
                                    borderColor: isSelected ? 'success.main' : 'divider',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        boxShadow: 6,
                                        zIndex: 10
                                    }
                                }}
                                onClick={() => setExpandedGame(isExpanded ? null : game.id)}
                            >
                                {/* Image with Overlay */}
                                <Box sx={{ position: 'relative', height: 140, overflow: 'hidden', borderRadius: '12px 12px 0 0' }}>
                                    <CardMedia
                                        component="img"
                                        height="140"
                                        image={getGameImage(game)}
                                        alt={game.name}
                                        sx={{
                                            objectFit: 'cover',
                                            transition: 'transform 0.3s ease'
                                        }}
                                    />
                                    
                                    {/* Gradient Overlay */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            background: 'linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.7))',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'flex-end',
                                            p: 1,
                                            gap: 0.5
                                        }}
                                    >
                                        {/* Category Badge */}
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            <Chip
                                                label={game.category}
                                                size="small"
                                                color={getCategoryColor(game.category)}
                                                sx={{ height: 20, fontSize: '0.65rem' }}
                                            />
                                            <Chip
                                                label={game.team_based ? 'Team' : '1v1'}
                                                size="small"
                                                variant="outlined"
                                                sx={{ 
                                                    height: 20, 
                                                    fontSize: '0.65rem',
                                                    bgcolor: alpha('#fff', 0.2),
                                                    color: 'white',
                                                    borderColor: alpha('#fff', 0.3)
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    {/* Selection Indicator */}
                                    {isSelected && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                bgcolor: 'success.main',
                                                borderRadius: '50%',
                                                p: 0.5,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <SelectedIcon sx={{ fontSize: 20, color: 'white' }} />
                                        </Box>
                                    )}
                                </Box>

                                {/* Card Content */}
                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: '0.875rem',
                                            mb: 0.5,
                                            textAlign: 'center',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {game.name}
                                    </Typography>

                                    {/* Toggle Switch */}
                                    <Box
                                        sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'center',
                                            pt: 1,
                                            mt: 1,
                                            borderTop: 1,
                                            borderColor: 'divider'
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Switch
                                            checked={isSelected}
                                            onChange={() => handleToggleGame(game)}
                                            size="small"
                                            color="success"
                                        />
                                    </Box>
                                </CardContent>

                                {/* Expanded Details */}
                                {isExpanded && isSelected && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            right: 0,
                                            bgcolor: 'background.paper',
                                            border: 1,
                                            borderColor: 'divider',
                                            borderRadius: 2,
                                            mt: 1,
                                            p: 2,
                                            boxShadow: 4,
                                            zIndex: 1000,
                                            minWidth: 250
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {game.description && (
                                            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                                                {game.description}
                                            </Typography>
                                        )}

                                        {/* Teams Display (if team-based) */}
                                        {game.team_based && preference && (
                                            <Box sx={{ mb: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <TeamIcon fontSize="small" color="primary" />
                                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                        Your Teams:
                                                    </Typography>
                                                </Box>
                                                {preference.team_names && preference.team_names.length > 0 ? (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {preference.team_names.map((teamName, index) => (
                                                            <Chip
                                                                key={index}
                                                                label={teamName}
                                                                size="small"
                                                                color="primary"
                                                                variant="outlined"
                                                            />
                                                        ))}
                                                    </Box>
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">
                                                        You're not in any teams yet
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}

                                        {/* Availability Toggle */}
                                        {preference && (
                                            <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={preference.available}
                                                            onChange={() => handleToggleAvailability(game.id, preference.available)}
                                                            size="small"
                                                            color="primary"
                                                        />
                                                    }
                                                    label={
                                                        <Typography variant="caption">
                                                            {preference.available ? 'Available for challenges' : 'Unavailable'}
                                                        </Typography>
                                                    }
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Card>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
}

export default GamePreferenceSelector;
